import {
  Checkbox,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  styled,
  Switch,
  Typography,
  Popover,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Header from "../Header";
import { format, parse } from "date-fns";
import OptionContainer from "./OptionContainer";
import OptionButton from "./OptionButton";
import StyledMenuButton from "./StyledMenuButton";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"; // Import CalendarToday icon
import { useSelector } from "react-redux";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker"; // MUI X StaticDatePicker
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"; // Adapter for MUI X DatePicker
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"; // Localization provider
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { DATE_FORMATS } from "../../../Utils/CONST";

// Styled iOS Switch (unchanged)
const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 48,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#1F2937",
      "& + .MuiSwitch-track": {
        backgroundColor: "#fff",
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#4B5563",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const PerformanceDashboardHeader = ({
  period,
  handlePeriodChange,
  propertyFilterLabel,
  allPropertyFilter,
  propertyFilterMenuAnchor,
  handlePropertyFilterMenuClose,
  handlePropertyFilterMenuOpen,
  toggleAllPropertyFilter,
  propertyFilterItemsTemp,
  handleSelectPropertyInFilter,
  toggleDoComparison,
  // 👇 New props
  selectedDate,
  handleDateSelection, // Change the handler for single date selection
}) => {
  const auth = useSelector((state) => state.auth);
  const defaultDateRef = useRef(new Date());

  // Local state for date picker popover
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const openDatePicker = (event) => setDatePickerAnchor(event.currentTarget);
  const closeDatePicker = () => setDatePickerAnchor(null);

  useEffect(() => {
    if (!selectedDate) {
      handleDateSelection(defaultDateRef.current);
    }
  }, [selectedDate, handleDateSelection]);

  const subtitle = (() => {
    const formattedTodaysDate = format(
      parse(selectedDate, DATE_FORMATS.DATE_1, new Date()),
      "MMMM dd"
    );
    switch (period.toLowerCase()) {
      case "today":
        return "Metric to-date for " + formattedTodaysDate;
      case "mtd":
        return "Month-to-date metrics for " + formattedTodaysDate;
      case "ytd":
        return "Year-to-date metrics for " + formattedTodaysDate;
      default:
        return "";
    }
  })();

  return (
    <Stack>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Header title={"Group performance dashboard"} subtitle={subtitle} />

        {/* OPTIONS ROW */}
        <Stack direction={"row"} spacing={2}>
          {/* Compare toggle */}
          <OptionContainer>
            <Stack direction={"row"} spacing={1} alignItems={"center"} px={0.5}>
              <IOSSwitch onClick={toggleDoComparison} />
              <Typography color={"#fff"} fontSize={"0.875rem"} fontWeight={600}>
                Compare with last year
              </Typography>
            </Stack>
          </OptionContainer>

          {/* Period buttons */}
          <OptionContainer>
            {["Today", "MTD", "YTD"].map((item) => (
              <OptionButton
                key={item}
                text={item}
                selected={item === period}
                onClick={() => handlePeriodChange(item)}
              />
            ))}
          </OptionContainer>

          {/* DATE PICKER BUTTON */}
          <OptionContainer>
            <StyledMenuButton
              variant="contained"
              onClick={openDatePicker}
              endIcon={<CalendarTodayIcon />} // Using the calendar icon here
            >
              {selectedDate
                ? format(new Date(selectedDate), DATE_FORMATS.DATE_1)
                : "Select Date"}
            </StyledMenuButton>
          </OptionContainer>

          {/* Property filter button */}
          <StyledMenuButton
            variant="contained"
            onClick={handlePropertyFilterMenuOpen}
            endIcon={
              !propertyFilterMenuAnchor ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowUpIcon />
              )
            }
          >
            <span
              style={{
                display: "block",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                flex: 1,
                textAlign: "left",
              }}
            >
              {propertyFilterLabel}
            </span>
          </StyledMenuButton>
        </Stack>
      </Stack>

      {/* DATE PICKER POPOVER */}
      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={closeDatePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={
              selectedDate ? new Date(selectedDate) : defaultDateRef.current
            }
            onChange={(newDate) => {
              if (newDate) {
                handleDateSelection(newDate);
                closeDatePicker();
              }
            }}
            slotProps={{ actionBar: { actions: [] } }}
          />
        </LocalizationProvider>
      </Popover>

      {/* PROPERTY FILTER MENU */}
      <Menu
        anchorEl={propertyFilterMenuAnchor}
        open={!!propertyFilterMenuAnchor}
        onClose={handlePropertyFilterMenuClose}
      >
        <MenuItem onClick={toggleAllPropertyFilter} dense>
          <Checkbox checked={allPropertyFilter} />
          <ListItemText primary="All Properties" />
        </MenuItem>
        {auth.hotels
          .filter((hotel) => "active" === hotel.status.toLowerCase())
          .map((hotel) => (
            <MenuItem
              key={hotel.hotelId}
              onClick={() => handleSelectPropertyInFilter(hotel.hotelId)}
              dense
            >
              <Checkbox
                checked={propertyFilterItemsTemp.includes(hotel.hotelId)}
              />
              <ListItemText primary={hotel.hotelName} />
            </MenuItem>
          ))}
      </Menu>
    </Stack>
  );
};

export default PerformanceDashboardHeader;
